import React, { useState, useEffect, useCallback } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { authenticatedFetchData, API_HOST } from '../../lib'
import { logError } from '@tomra/datadog-browser-logging'
import { Button } from '@tomra/design-system-react/button'

export const UsersPage = () => {
  const { query } = useParams()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState<string>(query ?? '')
  const [userList, setUserList] = useState<UserInfoType[] | null>(null)
  const [status, setStatus] = useState<RequestStatusType>('pending')

  const handleUserSearchResults = useCallback((userList: UserInfoType[]) => {
    setStatus('idle')
    setUserList(userList)
  }, [])

  const handleError = useCallback((error: Error) => {
    if (error.name !== 'AbortError') {
      setStatus('failure')
      logError(new Error('Failed to fetch user list'), error)
    }
  }, [])

  const searchUser = () => {
    setUserList(null)
    setStatus('pending')

    authenticatedFetchData(API_HOST + '/mytomra/v1.0/hotlinetool/usersearch?search=' + encodeURIComponent(searchTerm))
      .run()
      .then(handleUserSearchResults)
      .catch(handleError)
  }

  useEffect(() => {
    setUserList(null)
    setStatus('pending')

    const f = authenticatedFetchData(
      API_HOST + '/mytomra/v1.0/hotlinetool/usersearch?search=' + encodeURIComponent(query || '')
    )
    f.run().then(handleUserSearchResults).catch(handleError)

    return () => f.abort()
  }, [query, handleError, handleUserSearchResults])

  const isSearching = status === 'pending'

  const userSearchForm = (
    <form
      className="w-1/2"
      onSubmit={e => {
        e.preventDefault()
        searchUser()
      }}
    >
      <label htmlFor="search" className="block mb-1 mb-1">
        Search
      </label>
      <div className="grid grid-cols-8 gap-x-4">
        <input
          id="search"
          type="search"
          className="col-span-6 border border-gray-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          value={searchTerm}
          disabled={isSearching}
          onChange={({ target }) => setSearchTerm(target.value)}
        />
        <Button
          type="submit"
          variant="primary"
          className="flex justify-center items-center col-span-2"
          disabled={isSearching}
        >
          {isSearching ? <div className="loadingSpinner" /> : 'Search'}
        </Button>
      </div>
    </form>
  )

  if (status === 'failure') {
    return (
      <div>
        <h1 className="sr-only">Users</h1>
        <div className="rounded-lg border-l-[3px] border-rose-500 bg-white p-4 text-left shadow-md dark:bg-gray-900 centerAbsolute">
          Whoopsie! Failed to fetch users. Please try again.
        </div>
      </div>
    )
  }

  const hasResults = userList && userList.length > 0
  const noResults = userList && userList.length === 0

  return (
    <div className="p-6">
      <h1 className="text-xl mb-4">Users</h1>

      {hasResults ? (
        <>
          {userSearchForm}

          <div className="rounded-2xl bg-white shadow-md dark:bg-gray-900 mt-4">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>User ID</th>
                  <th>App name</th>
                  <th>Phone number</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {userList?.map(({ id, firstName, email, appContext, phoneNumber }) => (
                  <tr
                    className="cursor-pointer"
                    data-testid={`u-${id}`}
                    key={`u-${id}`}
                    onClick={() => navigate(`/user/${id}`)}
                  >
                    <td>{firstName}</td>
                    <td>{email}</td>
                    <td>{id}</td>
                    <td>{appContext}</td>
                    <td>{phoneNumber}</td>
                    <td>
                      <div className="flex justify-end">
                        <Link to={`/user/${id}`} aria-label={`See details for ${firstName}`}>
                          <span className="icon-[fluent--arrow-right-24-regular] size-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="flex flex-col">
          {userSearchForm}

          {noResults && (
            <>
              <p className="mt-4 font-bold">Sorry, we couldn&apos;t find that user.</p>
              <p>Try to include the entire email or first name and last name in the search.</p>
            </>
          )}

          {isSearching && (
            <div className="flex justify-center mt-4">
              <div className="loadingSpinner" aria-label="Please wait" />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
